import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from 'ui/Modal';
import api from 'config/axiosConfig';
import { Settings, X, Check } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from 'ui/Avatar';
import dayjs from 'config/dayjsConfig';
import { getEntityDetails, getActorDetails } from 'util/getObjectDetails';
import LoadingSkeleton from 'ui/LoadingSkeleton';
//import { useUser } from 'util/UserContext';
interface NotificationProps {
    id: number | string;
    user_id?: number;
    actor_id: number;
    actor_name: string;
    actor_type: 'user' | 'team';
    actor_display_name: string;
    actor_team_name: string;
    requestor_type?: 'user' | 'team';
    requestor_id?: number;
    requestor_name?: string;
    requestor_properties_public?: any;
    requestee_type?: 'user' | 'team';
    requestee_id?: number;
    requestee_name?: string;
    requestee_properties_public?: any;
    related_entity_type?: string;
    related_entity_id?: number;
    actor_properties_public: {
        avatar_key?: string;
        avatar_url?: string;
        is_private?: boolean;
    };
    entity_name: string;
    entity_properties_public: {
        avatar_key?: string;
        avatar_url?: string;
        is_private?: boolean;
    };
    notification_type: 'follow_new' | 'follow_request' | 'follow_accept' | 'coauthor_invite' | 'team_join_request' | 'team_invite';
    entity_type?: string;
    entity_id?: number;
    is_read: boolean;
    request_id?: number;
    request_status?: string;
    properties: {
        [key: string | number]: any;
    };
    created_at: string;
    updated_at?: string;
}

interface Props {
    onClose: () => void;
    onNotificationsRead: (count: number) => void;
}

const NotificationsModal: React.FC<Props> = ({ onClose, onNotificationsRead }) => {
    //const { currentUser } = useUser();
    const [notifications, setNotifications] = useState<NotificationProps[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const navigate = useNavigate();
    const IMG_CDN = process.env.REACT_APP_IMAGE_CDN;

    // Add state to track loaded entity details
    const [entityDetails, setEntityDetails] = useState<Record<string, any>>({});
    const [notificationEntityDetails, setNotificationEntityDetails] = useState<Record<string, any>>({});

    const fetchNotifications = useCallback(async (pageNum: number) => {
        try {
            setLoading(true);
            const response = await api.get('/api/notifications', {
                params: {
                    limit: 20,
                    offset: (pageNum - 1) * 20
                }
            });
            
            if (response.data.length < 20) {
                setHasMore(false);
            }


            setNotifications(prevNotifications => {
                const newNotifications = response.data.filter(
                    (newNotif: NotificationProps) => !prevNotifications.some(notif => notif.id === newNotif.id)
                );
                return [...prevNotifications, ...newNotifications];
            });
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        setNotifications([]);
        setPage(1);
        setHasMore(true);
        fetchNotifications(1);
    }, [fetchNotifications]);

    useEffect(() => {
        if (page > 1) {
            fetchNotifications(page);
        }
    }, [page, fetchNotifications]);

    const observer = useRef<IntersectionObserver | null>(null);
    const lastNotificationElementRef = useCallback((node: HTMLDivElement | null) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        }, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const handleMarkAllAsRead = async () => {
        try {
            await api.patch('/api/notifications/read/all');
            setNotifications(prevNotifications => 
                prevNotifications.map(notif => ({ ...notif, is_read: true }))
            );
            const unreadCount = notifications.filter(n => !n.is_read).length;
            onNotificationsRead(unreadCount);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const markNotificationAsRead = async (notificationId: number) => {
        try {
            await api.patch('/api/notifications/read', {
                notification_ids: [notificationId]
            });
            setNotifications(prevNotifications =>
                prevNotifications.map(notif =>
                    notif.id === notificationId ? { ...notif, is_read: true } : notif
                )
            );
            onNotificationsRead(1);
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const loadEntityDetails = useCallback(async (notification: NotificationProps) => {
        const { entity_type, entity_id, request_status, related_entity_type, related_entity_id } = notification;
        if (!related_entity_type || !related_entity_id || !entity_type || !entity_id || !request_status) return;

        const cacheKey = `${related_entity_type}-${related_entity_id}`;
        if (entityDetails[cacheKey]) return;

        const notificationCacheKey = `${entity_type}-${entity_id}`;
        if (notificationEntityDetails[notificationCacheKey]) return;

        try {
            const details = related_entity_type === 'team' || related_entity_type === 'user'
                ? await getActorDetails(related_entity_type, related_entity_id)
                : await getEntityDetails(related_entity_type, related_entity_id);
            const notificationEntity = entity_type === 'team' || entity_type === 'user'
                ? await getActorDetails(entity_type, entity_id)
                : await getEntityDetails(entity_type, entity_id);

            setEntityDetails(prev => ({
                ...prev,
                [cacheKey]: details
            }));
            setNotificationEntityDetails(prev => ({
                ...prev,
                [notificationCacheKey]: notificationEntity
            }));
            //console.log('loaded entityDetails', entityDetails);
            //console.log('loaded notificationEntityDetails', notificationEntityDetails);
        } catch (error) {
            console.error('Error loading entity details:', error);
        }
    }, [entityDetails, notificationEntityDetails]);

    const getNotificationAvatar = (notification: NotificationProps) => {
        const { entity_type, request_status, updated_at } = notification;
        // Get cached entity details
        const cacheKey = notification.related_entity_type && notification.related_entity_id
            ? `${notification.related_entity_type}-${notification.related_entity_id}`
            : null;
        const entityDetail = cacheKey ? entityDetails[cacheKey] : null;
        switch (request_status) {            
            case 'accepted':
                if (!updated_at) {
                    return (
                        <>
                            {!notification.updated_at || !notification.properties?.request_status ? (
                                <>
                                {notification.related_entity_type === 'team' ? (
                                    <a href={`/${notification.entity_type === 'user' ? 'user' : 'partea'}/${notification.entity_name}`}>
                                        <Avatar className="size-10 flex-shrink-0">
                                            <AvatarImage
                                            src={notification.entity_properties_public?.avatar_key ? 
                                                `${IMG_CDN}/${notification.entity_properties_public.avatar_key}` : 
                                                undefined}
                                            alt={notification.entity_name}
                                        />
                                            <AvatarFallback>
                                                {notification.entity_name.charAt(0)}
                                            </AvatarFallback>
                                        </Avatar>
                                    </a>
                                ) : (
                                    <a href={`/${notification.entity_type === 'user' ? 'user' : 'partea'}/${notification.entity_name}`}>
                                        <Avatar className="size-10 flex-shrink-0">
                                            <AvatarImage
                                            src={notification.entity_properties_public?.avatar_key ? 
                                                `${IMG_CDN}/${notification.entity_properties_public.avatar_key}` : 
                                                undefined}
                                            alt={notification.entity_name}
                                        />
                                            <AvatarFallback>
                                                {notification.entity_name.charAt(0)}
                                            </AvatarFallback>
                                        </Avatar>
                                    </a>
                                )}
                                </>
                            ) : (
                                (notification.requestee_type === 'user' ? (
                                    <a href={`/user/${notification.entity_name}`}>
                                        <Avatar className="size-10 flex-shrink-0">
                                            <AvatarImage
                                            src={notification.entity_properties_public?.avatar_key ? 
                                                `${IMG_CDN}/${notification.entity_properties_public.avatar_key}` : 
                                                undefined}
                                            alt={notification.entity_name}
                                        />
                                            <AvatarFallback>
                                                {notification.entity_name.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                    </a>
                                    ) : (
                                    <a href={`/partea/${notification.entity_name}`}>
                                        <Avatar className="size-10 flex-shrink-0">
                                            <AvatarImage
                                            src={notification.entity_properties_public?.avatar_key ? 
                                                `${IMG_CDN}/${notification.actor_properties_public.avatar_key}` : 
                                                undefined}
                                            alt={notification.entity_name}
                                        />
                                            <AvatarFallback>
                                                {notification.entity_name.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                    </a>
                                ))                                
                            )}
                        </>
                    )
                } else {
                    return (
                        <>
                            {notification.requestee_id ? (
                                <>
                                {notification.requestee_type === 'user' ?
                                    <a href={`/user/${notification.requestor_name}`}>
                                        <Avatar className="size-10 flex-shrink-0">
                                            <AvatarImage
                                            src={notification.requestor_properties_public?.avatar_key ? 
                                                `${IMG_CDN}/${notification.requestor_properties_public.avatar_key}` : 
                                                undefined}
                                            alt={notification.requestor_name}
                                        />
                                            <AvatarFallback>
                                                {notification.requestor_name?.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                    </a>
                                 : 
                                    <a href={`/partea/${notification.requestee_name}`}>
                                        <Avatar className="size-10 flex-shrink-0">
                                            <AvatarImage
                                            src={notification.requestee_properties_public?.avatar_key ? 
                                                `${IMG_CDN}/${notification.requestee_properties_public.avatar_key}` : 
                                                undefined}
                                            alt={notification.requestee_name}
                                        />
                                            <AvatarFallback>
                                                {notification.requestee_name?.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                    </a>
                                }
                                </>
                            ) : (
                                    <a href={`/${entity_type === 'user' ? 'user' : 'partea'}/${notification.entity_name}`}>
                                        <Avatar className="size-10 flex-shrink-0">
                                            <AvatarImage
                                            src={notification.entity_properties_public?.avatar_key ? 
                                                `${IMG_CDN}/${notification.entity_properties_public.avatar_key}` : 
                                                undefined}
                                            alt={notification.entity_name}
                                        />
                                            <AvatarFallback>
                                                {notification.entity_name.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                    </a>
                                )
                            }
                        </>
                    )
                }            
            case 'pending':
                if (entity_type === 'team' || notification.related_entity_type === 'team') {
                    return (
                        <>
                            {notification.related_entity_type !== 'team' ? (
                                <a href={`/user/${notification.actor_name}`}>
                                    <Avatar className="size-10 flex-shrink-0">
                                        <AvatarImage
                                        src={notification.actor_properties_public?.avatar_key ? 
                                            `${IMG_CDN}/${notification.actor_properties_public.avatar_key}` : 
                                            undefined}
                                        alt={notification.actor_name}
                                    />
                                        <AvatarFallback>
                                            {notification.actor_name.charAt(0).toUpperCase()}
                                        </AvatarFallback>
                                    </Avatar>
                                </a>
                            ) : (
                                    <a href={`/partea/${entityDetail?.entity_name}`}>
                                        <Avatar className="size-10 flex-shrink-0">
                                            <AvatarImage
                                            src={entityDetail?.properties_public?.avatar_key ? 
                                                `${IMG_CDN}/${entityDetail?.properties_public.avatar_key}` : 
                                                undefined}
                                            alt={entityDetail?.entity_name}
                                        />
                                            <AvatarFallback>
                                                {entityDetail?.entity_name.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                    </a>
                                )
                            }   
                        </>
                    );
                } else {
                    return (
                        <>
                            {!notification.updated_at ? (
                                <a href={`/user/${notification.actor_name}`}>
                                    <Avatar className="size-10 flex-shrink-0">
                                        <AvatarImage
                                        src={notification.actor_properties_public?.avatar_key ? 
                                            `${IMG_CDN}/${notification.actor_properties_public.avatar_key}` : 
                                            undefined}
                                        alt={notification.actor_name}
                                    />
                                        <AvatarFallback>
                                            {notification.actor_name.charAt(0).toUpperCase()}
                                        </AvatarFallback>
                                    </Avatar>
                                </a>
                            ) : (
                                    <a href={`/user/${notification.entity_name}`}>
                                        <Avatar className="size-10 flex-shrink-0">
                                            <AvatarImage
                                            src={notification.entity_properties_public?.avatar_key ? 
                                                `${IMG_CDN}/${notification.entity_properties_public.avatar_key}` : 
                                                undefined}
                                            alt={notification.entity_name}
                                        />
                                            <AvatarFallback>
                                                {notification.entity_name.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                    </a>
                                )
                            }
                        </>
                    )
                }
                            
            default:
                if (entity_type === 'team') {
                    return (
                        <>
                            {notification.actor_type === 'user' ? (
                                <a href={`/user/${notification.actor_name}`}>
                                    <Avatar className="size-10 flex-shrink-0">
                                        <AvatarImage
                                        src={notification.actor_properties_public?.avatar_key ? 
                                            `${IMG_CDN}/${notification.actor_properties_public.avatar_key}` : 
                                            undefined}
                                        alt={notification.actor_name}
                                    />
                                        <AvatarFallback>
                                            {notification.actor_name.charAt(0).toUpperCase()}
                                        </AvatarFallback>
                                    </Avatar>
                                </a>
                            ) : (
                                    <a href={`/partea/${notification.entity_name}`}>
                                        <Avatar className="size-10 flex-shrink-0">
                                            <AvatarImage
                                            src={notification.entity_properties_public?.avatar_key ? 
                                                `${IMG_CDN}/${notification.entity_properties_public.avatar_key}` : 
                                                undefined}
                                            alt={notification.entity_name}
                                        />
                                            <AvatarFallback>
                                                {notification.entity_name.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                    </a>
                                )
                            }   
                        </>
                    );
                } else {
                    return (
                        <>
                            {notification.updated_at || !notification.properties?.request_status ? (
                                <a href={`/user/${notification.actor_name}`}>
                                    <Avatar className="size-10 flex-shrink-0">
                                        <AvatarImage
                                        src={notification.actor_properties_public?.avatar_key ? 
                                            `${IMG_CDN}/${notification.actor_properties_public.avatar_key}` : 
                                            undefined}
                                        alt={notification.actor_name}
                                    />
                                        <AvatarFallback>
                                            {notification.actor_name.charAt(0).toUpperCase()}
                                        </AvatarFallback>
                                    </Avatar>
                                </a>
                            ) : (
                                    <a href={`/user/${notification.entity_name}`}>
                                        <Avatar className="size-10 flex-shrink-0">
                                            <AvatarImage
                                            src={notification.entity_properties_public?.avatar_key ? 
                                                `${IMG_CDN}/${notification.entity_properties_public.avatar_key}` : 
                                                undefined}
                                            alt={notification.entity_name}
                                        />
                                            <AvatarFallback>
                                                {notification.entity_name.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                    </a>
                                )
                            }
                        </>
                    )
                }
        }
    }

    const getNotificationMessage = (notification: NotificationProps) => {
        const { notification_type, entity_type, entity_id, request_status, related_entity_type, related_entity_id } = notification;
        
        // Get cached entity details
        const cacheKey = related_entity_type && related_entity_id
            ? `${related_entity_type}-${related_entity_id}`
            : null;
        const notificationCacheKey = entity_type && entity_id 
            ?`${entity_type}-${entity_id}`
            : null;
        const entityDetail = cacheKey ? entityDetails[cacheKey] : null;
        const notificationEntity = notificationCacheKey ? notificationEntityDetails[notificationCacheKey] : null;
        //console.log('notificationEntity', notificationEntity);
        //console.log('entityDetail', entityDetail);
        switch (notification_type) {
            case 'follow_new':
                return (related_entity_type === 'team' 
                    ? 
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {' '} started following partea {' '}
                            <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                {notificationEntity?.entity_name}
                            </a>
                        </span>
                    </span>
                    : 
                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                            {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                        </a>
                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            {` `} started following you
                        </span>
                    </span>
                );
            
            case 'follow_request':
                if (request_status === 'pending') {
                    return (related_entity_type === 'team' ? (
                            <>
                            <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                    {notification.actor_name}
                                </a>
                                <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    {' '} wants to follow partea {' '}
                                    <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                        {notificationEntity?.entity_name}
                                    </a>
                                </span>
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                    {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                </a>
                                <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                   {` `} wants to follow you
                                </span>
                            </span>
                        </>
                    ));
                } else if (request_status === 'accepted') {
                    return (related_entity_type === 'team' ? (
                        <>
                        {notification.updated_at ? (
                            <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                <a href={`/${notification.entity_type === 'user' ? 'user' : 'partea'}/${notification.entity_name}`} className='font-semibold'>
                                    {notification.entity_name}
                                </a>
                                <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    {` `} started following partea {' '}
                                    <a href={`/partea/${entityDetail?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                        {entityDetail?.entity_name}
                                    </a>
                                    {` `} {`(accepted by`} {' '}
                                    <a href={`/user/${notification.actor_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                        {notification.actor_name}{`)`}
                                    </a>
                                </span>
                            </span>
                        ) : (
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        you started following partea {' '}
                                        <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {notificationEntity?.entity_name}
                                        </a>
                                        {` `} {`(accepted by`} {' '}
                                        <a href={`/user/${notification.actor_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {notification.actor_name}{`)`}
                                        </a>
                                    </span>
                                </span>
                            )}
                        </>
                    ) : (
                        <>
                        {notification.updated_at ? (
                            <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                <a href={`/user/${notification.entity_name}`} className='font-semibold'>
                                    {notification.entity_name}
                                </a>
                                <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                {' '} started following you
                                </span>
                            </span>
                        ) : (
                            <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    you started following {' '}
                                </span>
                                <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                    {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                </a>
                            </span>
                        )}
                        </>
                    ));
                } else {
                    return null;
                }
            case 'coauthor_invite':
                switch (request_status) {
                    case 'pending':
                        return (entity_type === 'team' ? (
                            <>
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                        {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                    </a>
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        {' '} wants partea {' '}
                                        <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {notificationEntity?.entity_name}
                                        </a>
                                        {' '} to collaborate on {' '}
                                        <a href={`/${related_entity_type}/${related_entity_id}`} className='external-link line-clamp-2'>
                                            {related_entity_type} {' '} {entityDetail?.entity_name || related_entity_type}
                                        </a>
                                    </span>
                                </span>
                            </>
                        ) : (
                            <>
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                        {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                    </a>
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    {' '} wants you to collaborate on {' '}
                                    <a href={`/${related_entity_type}/${related_entity_id}`} className='external-link line-clamp-2'>
                                        {related_entity_type} {' '} {entityDetail?.entity_name || related_entity_type}
                                    </a>
                                    </span>
                                </span>
                            </>
                        ));
                    case 'accepted':
                        return (entity_type === 'team' ? (
                            <>
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    Partea {' '}
                                        <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {notificationEntity?.entity_name}
                                        </a>
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        {' '} is now collaborating with you on {' '}
                                        <a href={`/${related_entity_type}/${related_entity_id}`} className='external-link line-clamp-2'>
                                            {related_entity_type} {' '} {entityDetail?.entity_name || related_entity_type}
                                        </a>
                                    </span>
                                </span>
                            </>
                        ) : (
                            <>{notification.updated_at ? (
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    {notification.requestee_type === 'team' ?<> Partea {` `}
                                    <a href={`/partea/${notification.requestee_name}`} className='font-semibold'>
                                        {notification.requestee_name}
                                    </a> {` `} is
                                    </> : 
                                    <>
                                    You are
                                    </>
                                    }
                                    {``} now collaborating with {' '}
                                    <a href={`/${notification.entity_type === 'user' ? 'user' : 'partea'}/${notification.entity_name}`} className='font-semibold'>
                                        {notification.entity_name}
                                    </a>
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                    {' '} on {' '}
                                    <a href={`/${related_entity_type}/${related_entity_id}`} className='external-link line-clamp-2'>
                                        {related_entity_type} {' '} {entityDetail?.entity_name || related_entity_type}
                                    </a>
                                    </span>
                                </span>
                            ) : (
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                        {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                    </a> 
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        {' '} is now collaborating with you on {' '}
                                        <a href={`/${related_entity_type}/${related_entity_id}`} className='external-link line-clamp-2'>
                                            {related_entity_type} {' '} {entityDetail?.entity_name || related_entity_type}
                                        </a>
                                    </span>
                                </span>
                            )}
                            </>
                        ));
                
                    default:
                        return 'requested to collaborate on'
                }
            case 'team_join_request':
                switch (request_status) {
                    case 'pending':
                        return (
                            <>
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                        {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                    </a>
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        {' '} wants to join partea {' '}
                                        <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {notificationEntity?.entity_name}
                                        </a>
                                    </span>
                                </span>
                            </>
                        );
                    case 'accepted':
                        return (
                                <>
                                {notification.updated_at ? (
                                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                        <a href={`/${notification.requestor_type === 'user' ? 'user' : 'partea'}/${notification.requestor_name}`} className='font-semibold'>
                                            {notification.requestor_name}
                                        </a>
                                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                            {' '} joined partea {' '}
                                            <a href={`/${related_entity_type}/${related_entity_id}`} className='font-semibold text-eoe-text dark:text-eoe-dark-text'>
                                                {entityDetail?.entity_name || related_entity_type}
                                            </a>
                                            {` `} {`(accepted by `}
                                            <a href={`/user/${notification.actor_name}`} className='font-semibold text-eoe-text dark:text-eoe-dark-text'>
                                                {notification.actor_name}
                                            </a>
                                            {`)`}
                                        </span>
                                    </span>
                                ) : (
                                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                            {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                        </a>
                                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                            {' '} accepted your request to join partea {' '}
                                            <a href={`/partea/${notification.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                                {notification.entity_name}
                                            </a>
                                        </span>
                                    </span>
                                )}
                                </>
                            );
                
                    default:
                        return 'requested to collaborate on'
                }
            case 'team_invite':
                switch (request_status) {
                    case 'pending':
                        return (
                                <>
                                    <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                        <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                            {notification.actor_name || notification.actor_team_name || notification.actor_display_name}
                                        </a>
                                        <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                            {' '} invited you to partea {' '}
                                            <a href={`/partea/${entityDetail?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                                {entityDetail?.entity_name}
                                            </a>
                                        </span>
                                    </span>
                                </>
                            );
                    case 'accepted':
                        return (
                            <>
                                <span className="font-medium text-sm text-eoe-text dark:text-eoe-dark-text">
                                    <a href={`/${notification.actor_type === 'user' ? 'user' : 'partea'}/${notification.actor_name}`} className='font-semibold'>
                                        {notification.actor_name || notification.actor_display_name}
                                    </a>
                                    <span className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                                        {' '} accepted your invite and joined partea {' '}
                                        <a href={`/partea/${notificationEntity?.entity_name}`} className='font-bold text-eoe-text dark:text-eoe-dark-text'>
                                            {notificationEntity?.entity_name}
                                        </a>
                                        {' '}for {' '}
                                        <a href={`/${related_entity_type}/${related_entity_id}`} className='external-link line-clamp-2'>
                                            {related_entity_type} {' '} {entityDetail?.entity_name || related_entity_type}
                                        </a>
                                    </span>
                                </span>
                            </>
                        );
                
                    default:
                        return 'requested to collaborate on'
                    }
            default:
                return 'interacted with you';
        }
    };

    const handleNotificationClick = async (notification: NotificationProps) => {
        if (!notification.is_read) {
            await markNotificationAsRead(notification.id as number);
        }
    };

    const handleRequestAction = async (notification: NotificationProps, accept: boolean) => {
        try {
            await api.patch(`/api/requests/${notification.request_id}`, {
                status: accept ? 'accepted' : 'rejected'
            });
            const newNotification = await api.get(`/api/notifications/${notification.id}`);
            //console.log('newNotification', newNotification);

            if (accept) {
                // Update notification status for accepted requests
                setNotifications(prevNotifications =>
                    prevNotifications.map(notif =>
                        notif.id === notification.id 
                            ? { 
                                ...notif, 
                                ...newNotification.data
                            } 
                            : notif
                    )
                );
            } else {
                // Remove rejected notifications
                setNotifications(prevNotifications =>
                    prevNotifications.filter(notif => parseInt(notif.id as string, 10) !== parseInt(notification.id as string, 10))
                );
            }

            // Mark notification as read
            await markNotificationAsRead(notification.id as number);
        } catch (error) {
            console.error('Error handling request action:', error);
            // Optionally add error handling UI feedback here
        }
    };

    const groupNotificationsByDate = (notifications: NotificationProps[]) => {
        const today = dayjs();
        return notifications.reduce((groups, notification) => {
            const notifDate = dayjs(notification.updated_at || notification.created_at);
            const key = notifDate.isSame(today, 'day') ? 'today' : 'earlier';
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(notification);
            return groups;
        }, {} as Record<string, NotificationProps[]>);
    };

    const groupedNotifications = groupNotificationsByDate(notifications);

    // Load entity details when notifications change
    useEffect(() => {
        notifications.forEach(notification => {
            loadEntityDetails(notification);
        });
    }, [notifications, loadEntityDetails]);

    return (
        <Modal 
            isOpen={true} 
            onClose={onClose}
            title={
                <div className="flex justify-between items-center w-full pr-8">
                    <span>Notifications</span>
                </div>
            }
        >
            <div className="flex flex-col divide-y divide-eoe-border dark:divide-eoe-dark-border max-h-[calc(100vh-8rem)] overflow-y-auto space-y-1 mt-1">
                {loading && notifications.length === 0 ? (
                    <LoadingSkeleton count={10} height={'h-20'} />
                ) : notifications.length === 0 ? (
                    <div className="my-2 text-left text-eoe-text dark:text-eoe-dark-text">
                        All caught up!
                    </div>
                ) : (
                    <>
                            <>
                                <div className="flex justify-between items-center w-full">
                                    <div className="font-medium text-eoe-text dark:text-eoe-dark-text">
                                        Today
                                    </div>
                                    <div className="flex items-center space-x-4">
                                        <button 
                                            className="text-eoe-text dark:text-eoe-dark-text hover:text-eoe-text-sub dark:hover:text-eoe-dark-text-sub"
                                            onClick={handleMarkAllAsRead}
                                        >
                                            Mark all as read
                                        </button>
                                        <button 
                                            className="p-1 rounded-full hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                                            onClick={() => {
                                                navigate('/settings/notifications');
                                                onClose();
                                            }}
                                        >
                                            <Settings className="size-5 stroke-eoe-text dark:stroke-eoe-dark-text" />
                                        </button>
                                    </div>
                                </div>
                                {groupedNotifications.today && groupedNotifications.today.length > 0 && groupedNotifications.today.map((notification, index) => (
                                    <div
                                        key={notification.id}
                                        ref={index === notifications.length - 1 ? lastNotificationElementRef : null}
                                        className={`py-2 flex items-start gap-3 rounded-md 
                                            ${notification.is_read ? 'cursor-normal' : 'cursor-pointer'}
                                            ${notification.is_read ? ' bg-eoe-bg dark:bg-eoe-dark-bg-hover hover:bg-eoe-bg-modal-hover hover:dark:bg-eoe-dark-bg-modal-hover' : ' hover:bg-eoe-bg hover:dark:bg-eoe-dark-bg-hover bg-eoe-bg-modal-hover dark:bg-eoe-dark-bg-modal-hover'}`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        {notification.request_status === 'pending' && (
                                            <>
                                                {getNotificationAvatar(notification)}
                                            </>
                                        )}
                                        
                                        {notification.request_status === 'accepted' && (
                                            <>                                                
                                                {getNotificationAvatar(notification)}
                                            </>
                                        )}

                                        <div className={`flex-1 min-w-0 ${notification.notification_type === 'coauthor_invite' ? 'flex flex-col gap-2' : ''}`}>
                                            <div className="flex items-baseline">
                                                {getNotificationMessage(notification)}
                                            </div>
                                            <span className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                                {dayjs(notification?.updated_at ||notification.created_at).fromNow()}
                                            </span>
                                            {notification.request_status === 'pending' && (
                                                <div className="flex gap-2 mt-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRequestAction(notification, true);
                                                        }}
                                                        className="btn-primary opacity-75 flex items-center gap-1 pr-2 pl-1 py-1 rounded-full bg-eoe-error hover:bg-eoe-error-hover text-white text-xs font-normal"
                                                    >
                                                        <Check className="w-4 h-4" />
                                                        Accept
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRequestAction(notification, false);
                                                        }}
                                                        className="btn-secondary opacity-75 flex items-center gap-1 pr-2 pl-1 py-1 rounded-full bg-eoe-error hover:bg-eoe-error-hover text-white text-xs font-normal"
                                                    >
                                                        <X className="w-4 h-4" />
                                                        Decline
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </>
                        
                        {groupedNotifications.earlier?.length > 0 && (
                            <>
                                <div className="py-2 font-medium text-eoe-text dark:text-eoe-dark-text">
                                    Earlier
                                </div>
                                {groupedNotifications.earlier.map((notification, index) => (
                                    <div
                                        key={notification.id}
                                        ref={index === notifications.length - 1 ? lastNotificationElementRef : null}
                                        className={`py-2 flex items-start gap-3 rounded-md 
                                            ${notification.notification_type === 'coauthor_invite' ? 'cursor-pointer' : 'cursor-pointer'}
                                            ${notification.is_read ? ' bg-eoe-bg dark:bg-eoe-dark-bg-hover hover:bg-eoe-bg-modal-hover hover:dark:bg-eoe-dark-bg-modal-hover' : ' hover:bg-eoe-bg hover:dark:bg-eoe-dark-bg-hover bg-eoe-bg-modal-hover dark:bg-eoe-dark-bg-modal-hover'}`}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        
                                        {getNotificationAvatar(notification)}
                                        
                                        <div className={`flex-1 min-w-0`}>
                                            <div className="flex items-baseline">
                                                {getNotificationMessage(notification)}
                                            </div>
                                            <span className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                                {dayjs(notification?.updated_at ||notification.created_at).fromNow()}
                                            </span>
                                            {notification.request_status === 'pending' && (
                                                <div className="flex gap-2 mt-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRequestAction(notification, true);
                                                        }}
                                                        className="btn-primary opacity-75 flex items-center gap-1 pr-2 pl-1 py-1 rounded-full bg-eoe-error hover:bg-eoe-error-hover text-white text-xs font-normal"
                                                    >
                                                        <Check className="w-4 h-4" />
                                                        Accept
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRequestAction(notification, false);
                                                        }}
                                                        className="btn-secondary opacity-75 flex items-center gap-1 pr-2 pl-1 py-1 rounded-full bg-eoe-error hover:bg-eoe-error-hover text-white text-xs font-normal"
                                                    >
                                                        <X className="w-4 h-4" />
                                                        Decline
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </>
                )}
                {loading && notifications.length > 0 && (
                    <div className="p-4 text-center text-eoe-text dark:text-eoe-dark-text">
                        Loading more...
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default NotificationsModal; 